import classNames from "classnames";
import { X } from "lucide-react";
import * as React from "react";

import { FieldTypeIcon } from "../fieldTypeIcons";
import { type FilterOption } from "../FilterDefinition";
import { type Filter } from "../FilterSchema";
import { formatFilterValue } from "../formatFilterValue";
import { comparatorLabelForFilter } from "../operators";

import styles from "./FilterChip.module.css";

export interface FilterChipProps {
  /** The filter row this chip represents. */
  filter: Filter;
  /**
   * Human label for `filter.field` (e.g. `"Dataset Id"`). Resolved upstream
   * by `<DataTable.ActiveFilters>` from the `FilterDefinition` list — the
   * chip only consumes a string so its prop surface stays honest.
   */
  fieldName: string;
  /**
   * Pre-resolved options for label-vs-value translation when the filter
   * is ``set``- or ``enum``-typed and its definition supplies labeled
   * options (chip text shows ``label`` instead of the opaque wire
   * ``value``). Resolved upstream so this component stays presentational
   * and never touches ``FilterDefinition`` itself.
   */
  options?: FilterOption[];
  /**
   * Called when the user clicks the `×` segment.
   */
  onRemove: () => void;
  /**
   * Called when the user clicks the chip body (field, comparator, or value
   * segment) — the edit gesture. When provided, the body renders as a
   * `<button>`; the caller anchors its edit popover on
   * `event.currentTarget`. When absent (e.g. no definition resolves for
   * the field), the body stays non-interactive.
   */
  onEdit?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Forwarded to `formatFilterValue` to control the multi-value collapse
   * threshold. Defaults to 2 (the helper's default).
   */
  valueLimit?: number;
}

/**
 * One chip in the active filters row. Four segments separated by full-height
 * dividers: `[type-icon Field] | [comparator] | [value] | [×]`. The value
 * segment is omitted entirely for valueless comparators (`IS_NULL`,
 * `IS_NOT_NULL`, date presets) — the comparator alone carries the semantic.
 *
 * The `×` removes the filter. With `onEdit` provided, the field, comparator,
 * and value segments become one edit button (the caller anchors its edit
 * popover on it); without it, they stay non-focusable `<span>`s.
 */
export function FilterChip({
  filter,
  fieldName,
  options,
  onRemove,
  onEdit,
  valueLimit,
}: FilterChipProps) {
  const cLabel = comparatorLabelForFilter(filter);
  const value = formatFilterValue(filter, { valueLimit, options });

  const body = (
    <>
      <span className={classNames(styles.segment, styles.segmentField)}>
        <span className={styles.fieldContent}>
          <FieldTypeIcon type={filter.type} className={styles.icon} />
          <span>{fieldName}</span>
        </span>
      </span>
      <span className={classNames(styles.segment, styles.segmentComparator)}>
        {cLabel}
      </span>
      {value !== null && (
        <span className={classNames(styles.segment, styles.segmentValue)}>
          <span className={styles.valueGroup}>
            <span className={styles.valueText}>{value.text}</span>
            {filter.type === "metric" && filter.unit && (
              <span className={styles.valueUnit}>{filter.unit}</span>
            )}
          </span>
          {value.more !== undefined && (
            <span className={styles.valueMore}>+{value.more} more</span>
          )}
        </span>
      )}
    </>
  );

  return (
    <span
      className={styles.chip}
      role="group"
      aria-label={`${fieldName} ${cLabel}`}
    >
      {onEdit ? (
        <button
          type="button"
          className={styles.body}
          onClick={onEdit}
          aria-haspopup="dialog"
          aria-label={`Edit ${fieldName} filter`}
        >
          {body}
        </button>
      ) : (
        body
      )}
      <button
        type="button"
        className={classNames(styles.segment, styles.segmentRemove)}
        onClick={onRemove}
        aria-label={`Remove ${fieldName} filter`}
      >
        <X />
      </button>
    </span>
  );
}
